import React, { useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { escalaDaJanela, escalaMaior } from './escalas';

// Foto ampliada num <dialog> nativo com showModal(): o navegador prende o
// foco, deixa o fundo inerte e põe a janela por cima de tudo. As setas são
// ouvidas no próprio <dialog>, nunca na janela inteira do navegador.
const JanelaDaFoto = ({ fotos, atual, rotulo, aoMudar, aoFechar }) => {
  const dialogo = useRef(null);
  const botaoFechar = useRef(null);
  const avisarFechamento = useRef(aoFechar);
  avisarFechamento.current = aoFechar;

  const total = fotos.length;
  const aberta = Number.isInteger(atual) && atual >= 0 && atual < total;
  const foto = aberta ? fotos[atual] : null;

  // O Esc passa pelo handler do React (teclas, que chama fechar). Quando é o
  // navegador que fecha a janela (CloseWatcher, botão voltar do Android) ou
  // alguém chama dialog.close(), o aviso vem pelo evento "close": é daí que a
  // galeria devolve o foco à miniatura.
  useEffect(() => {
    const dlg = dialogo.current;
    const avisar = () => avisarFechamento.current();
    dlg.addEventListener('close', avisar);
    return () => dlg.removeEventListener('close', avisar);
  }, []);

  useEffect(() => {
    const dlg = dialogo.current;
    if (!aberta || dlg.hasAttribute('open')) return;
    if (typeof dlg.showModal === 'function') {
      dlg.showModal();
    } else {
      // Navegador sem <dialog> modal (iOS anterior ao 15.4, WebView antiga):
      // abre sem prender o foco; o Esc continua valendo pelo onKeyDown.
      dlg.setAttribute('open', '');
    }
    if (botaoFechar.current) botaoFechar.current.focus();
  }, [aberta]);

  const fechar = () => {
    const dlg = dialogo.current;
    if (typeof dlg.close === 'function') {
      dlg.close(); // dispara "close", que avisa a galeria
    } else {
      dlg.removeAttribute('open');
      avisarFechamento.current();
    }
  };

  const ir = (passo) => aoMudar((atual + passo + total) % total);

  const teclas = (e) => {
    if (!aberta) return;
    // Alt+←/→ (voltar e avançar do navegador), Ctrl/Cmd+←/→ e afins: atalhos
    // do navegador, que a janela não engole.
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      fechar();
    } else if (total > 1 && e.key === 'ArrowRight') {
      e.preventDefault();
      ir(1);
    } else if (total > 1 && e.key === 'ArrowLeft') {
      e.preventDefault();
      ir(-1);
    }
  };

  const grande = foto ? escalaDaJanela(foto) : null;
  const maior = foto ? escalaMaior(foto) : null;
  const descricao = foto?.description || '';

  // O leitor de tela lê o contador e a legenda ao abrir: é o que diz qual foto
  // abriu. Ids próprios por instância, para duas galerias na mesma página.
  const base = useId();
  const idContador = `${base}-contador`;
  const idLegenda = `${base}-legenda`;
  let descritaPor;
  if (foto && grande)
    descritaPor = descricao ? `${idContador} ${idLegenda}` : idContador;

  return (
    <dialog
      ref={dialogo}
      className="ag-galeria__janela"
      aria-label={rotulo}
      aria-describedby={descritaPor}
      onKeyDown={teclas}
    >
      {foto && grande && (
        <>
          <div className="ag-galeria__topo">
            <p id={idContador} className="ag-galeria__contador">
              {`${atual + 1} de ${total}`}
            </p>
            {/* A região viva é esta, separada do contador visível: quem troca
                de foto pelo botão ou pelas setas ouve o número e a descrição
                da nova foto. Fora do contador porque o aria-describedby da
                janela aponta para ele e leria a descrição duas vezes ao abrir.
                O elemento fica montado ao navegar (sem key por foto): trocar o
                texto é o que dispara o anúncio. */}
            <p
              className="ag-galeria__so-leitor"
              aria-live="polite"
              aria-atomic="true"
            >
              {`${atual + 1} de ${total}${descricao ? `: ${descricao}` : ''}`}
            </p>
            <button
              ref={botaoFechar}
              type="button"
              className="ag-galeria__botao"
              onClick={fechar}
            >
              Fechar
            </button>
          </div>
          <figure className="ag-galeria__figura">
            {/* eslint-disable-next-line no-restricted-syntax */}
            <img
              key={foto['@id']}
              className="ag-galeria__foto"
              src={grande.url}
              width={grande.width}
              height={grande.height}
              alt={descricao}
            />
            {descricao && (
              <figcaption id={idLegenda} className="ag-galeria__legenda">
                {descricao}
              </figcaption>
            )}
          </figure>
          <div className="ag-galeria__navegacao">
            {total > 1 && (
              <button
                type="button"
                className="ag-galeria__botao"
                aria-label="Foto anterior"
                onClick={() => ir(-1)}
              >
                <span aria-hidden="true">←</span> Anterior
              </button>
            )}
            {maior && (
              <a className="ag-galeria__ampliar" href={maior.url}>
                Ver em tamanho maior
              </a>
            )}
            {total > 1 && (
              <button
                type="button"
                className="ag-galeria__botao"
                aria-label="Próxima foto"
                onClick={() => ir(1)}
              >
                Próxima <span aria-hidden="true">→</span>
              </button>
            )}
          </div>
        </>
      )}
    </dialog>
  );
};

JanelaDaFoto.propTypes = {
  fotos: PropTypes.arrayOf(PropTypes.object).isRequired,
  atual: PropTypes.number,
  rotulo: PropTypes.string.isRequired,
  aoMudar: PropTypes.func.isRequired,
  aoFechar: PropTypes.func.isRequired,
};

export default JanelaDaFoto;
